import { useRef } from "react";
import JoinedTableNames from "../../../reservations/components/seat/JoinedTableNames";
import { TableCardClosingLabel } from "./TableCardClosingLabel";
import { Highlight, highlightParts } from "./tableCardHighlight";
import { dottedInitialsOf } from "./useBatchWaiterLabels";
import { useTableCardFit } from "./layout/useTableCardFit";
import type { TableCardFact } from "./cardInfo/tableCardFacts";
import { CardFactsMeasureLayer, FactLine } from "./cardInfo/TableCardFactParts";

// Кегелът на сумата — общ за видимото и за слоя за мерене
// (`CardFactsMeasureLayer`); размерът ѝ е по `compact`.
const TOTAL_WEIGHT = "font-pos-mono font-semibold leading-none";

export interface TableCardProps {
  /** Ref към бутона на картата — за стрелките и скрола до избраната маса. */
  cardRef: (element: HTMLButtonElement | null) => void;
  name: string;
  /** Масите, които сметката държи сама; при повече от една („Една сметка" за
   *  резервация на няколко маси) името е всичките — „14 | 13 | 12". */
  tableNames?: string[];
  total: number;
  waiterLabel: string | null;
  /** Закачен клиент — името на масата е в акцентен цвят. */
  hasClient: boolean;
  isSelected: boolean;
  isClosedEmpty: boolean;
  /** Закриването на масата още чака фискалния принтер (модалът „Закрий" вече
   *  е затворен): картата е сива, вместо сумата — „Закрива се" със спинер,
   *  и не се кликва. */
  isClosing?: boolean;
  /** Фактите за сметката в реда на четене (`tableCardFactsOf`): резервация,
   *  гости, „Карта — без бон", депозит, клиент, отстъпки. */
  facts: TableCardFact[];
  searchTerm: string;
  compact: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
}

/** Картата на една маса в списъка на „Маркиране" (`TableList`) на телефон и
 *  таблет (`compact`); на десктоп списъкът ползва `DesktopTableCard` — с
 *  размера от дръжките. Името на масата, сервитьорът (на чужда маса — само
 *  инициалите) и сумата; фактите за сметката — на един ред до сумата, всички
 *  в един вид (текст, стойности или иконки), а не стигат ли там — на ред над
 *  нея (`planCardFacts`, `useTableCardFit`): кутийката расте само тогава. Маса
 *  без факти — както без тях; „Закрива се" — само името и етикетът. */
export function TableCard({
  cardRef,
  name,
  tableNames,
  total,
  waiterLabel,
  hasClient,
  isSelected,
  isClosedEmpty,
  isClosing = false,
  facts,
  searchTerm,
  compact,
  onClick,
  onDoubleClick,
}: TableCardProps) {
  const cardElementRef = useRef<HTMLButtonElement | null>(null);
  // Всичко, от което зависят мерките в картата (`useTableCardFit`); ширината
  // е на релсата, височината следва съдържанието.
  const plan = useTableCardFit(
    cardElementRef,
    "compact",
    JSON.stringify([name, tableNames, total, waiterLabel, isClosing, facts, searchTerm]),
    facts,
  );

  // Избраната маса е с маркиран фон и акцентна рамка; закритата празна
  // (нефискализирана) — леко приглушен фон + сиви текстове, за да се
  // отличава от отворена празна сметка (напр. след пълно сторно). Масата в
  // процес на закриване е сива дори избрана — вместо сумата стои „Закрива се".
  const surface = isClosing
    ? "bg-pos-closed-bg border-pos-closed-brd"
    : isSelected
      ? "bg-pos-marked border-pos-accent"
      : isClosedEmpty
        ? "bg-pos-closed-bg border-pos-closed-brd"
        : "bg-pos-card border-pos-line";
  // Име с клиент носи акцентен цвят — визуален сигнал, че има закачен клиент.
  const nameColor = isClosedEmpty
    ? "text-pos-dim"
    : hasClient
      ? "text-pos-accent-ink"
      : "text-pos-ink";
  const joinedNames = tableNames && tableNames.length > 1 ? tableNames : null;
  const nameContent = joinedNames ? (
    <JoinedTableNames
      names={joinedNames}
      renderName={(tableName) => highlightParts(tableName, searchTerm)}
    />
  ) : (
    highlightParts(name, searchTerm)
  );
  const hasFacts = facts.length > 0;
  const nameSize = compact ? "text-sm" : "text-[21px]";
  const totalSize = compact ? "text-[13px]" : "text-[19px]";
  const nameClass = `flex-1 min-w-0 truncate font-bold leading-[1.2] ${nameColor} ${nameSize}`;
  const factLine =
    plan.line.length > 0 ? (
      <FactLine
        items={plan.line}
        form={plan.lineForm}
        facts={facts}
        hidden={plan.hidden}
        size="compact"
        searchTerm={searchTerm}
        className={plan.lineAboveSum ? "" : "-mb-px"}
      />
    ) : null;

  return (
    <button
      ref={(element) => {
        cardElementRef.current = element;
        cardRef(element);
      }}
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      disabled={isClosing}
      // `group/tablecard` — `data-measuring` (`useTableCardFit`) и
      // `data-selected` („+N"); `relative` — слоят за мерене. Само при факти —
      // иначе картата е както без тях.
      data-selected={hasFacts && isSelected ? "" : undefined}
      className={`min-w-0 text-left flex flex-col border-[1.5px] ${surface} ${
        isClosing ? "cursor-not-allowed" : "cursor-pointer"
      } ${
        compact
          ? "px-2 py-1.5 rounded-lg gap-1"
          : "rounded-[14px] p-[8px_14px] min-h-[64px] gap-1.5"
      }${hasFacts ? " group/tablecard relative" : ""}`}
    >
      {/* Горен ред: името на масата + (на чужда маса) сервитьорът, воден по
          ГОРНИЯ ръб на името (mt компенсира разликата в half-leading + cap
          height на двата кегела) — дългите текстове се режат с „…" и НЕ
          излизат от картата. leading-[1.2] (не none) — truncate клипва
          десцендерите при по-стегнат ред */}
      <div className="flex items-start gap-2 w-full min-w-0">
        {joinedNames ? (
          <span className={`inline-block ${nameClass}`}>{nameContent}</span>
        ) : (
          <Highlight text={name} term={searchTerm} className={nameClass} />
        )}
        {/* При закриване картата показва само името и „Закрива се" — без
            сервитьора името има цялата ширина („до телевизора") */}
        {/* Името на масата е с предимство: на тясната (телефонна) карта —
            само инициалите на сервитьора („Т.П."), цялото име е в подсказката */}
        {waiterLabel && !isClosing && (
          compact ? (
            <span
              title={waiterLabel}
              className="mt-px shrink-0 text-[10px] leading-[1.2] text-pos-muted"
            >
              {dottedInitialsOf(waiterLabel)}
            </span>
          ) : (
            <Highlight
              text={waiterLabel}
              term={searchTerm}
              className="mt-[3px] max-w-[55%] shrink-0 truncate text-[12px] leading-[1.2] text-pos-muted"
            />
          )
        )}
      </div>

      {/* Фактите, които не се събраха до сумата — на ред над нея (само тогава
          кутийката расте). */}
      {plan.lineAboveSum && factLine}

      {/* Долен ред: фактите (ако има) и сумата. items-end + leading-none:
          долният ръб на фактите лежи на долния ръб на тотала (-mb-px —
          оптично, базовите линии на двата кегела, като на десктоп) */}
      <div className="mt-auto flex items-end gap-2 w-full min-w-0">
        {isClosing ? (
          <TableCardClosingLabel compact={compact} />
        ) : (
          <>
            {!plan.lineAboveSum && factLine}
            <Highlight
              text={total.toFixed(2)}
              term={searchTerm}
              className={`ml-auto shrink-0 ${TOTAL_WEIGHT} ${
                isClosedEmpty ? "text-pos-dim" : "text-pos-ink"
              } ${totalSize}`}
            />
          </>
        )}
      </div>

      {hasFacts && (
        <CardFactsMeasureLayer
          facts={facts}
          size="compact"
          searchTerm={searchTerm}
          sumClassName={`${TOTAL_WEIGHT} ${totalSize}`}
          total={total.toFixed(2)}
        />
      )}
    </button>
  );
}
